import { useCallback, useEffect, useState } from "react";
import { EnvelopeSimple, PencilSimple, Trash, WarningCircle, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  addKnownSender,
  editKnownSender,
  listKnownSenders,
  removeKnownSender,
} from "@/lib/knownSendersApi";

const detailFromError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const joined = detail
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.msg || item?.message || "";
      })
      .filter(Boolean)
      .join(", ");
    if (joined) return joined;
  }
  return fallback;
};

const mutationMessage = (error, action) => {
  const status = error?.response?.status;
  if (status === 400) return detailFromError(error, "Enter a valid email address.");
  if (status === 403) return detailFromError(error, "Only the household operator can manage known senders.");
  if (status === 404) return detailFromError(error, "That sender no longer exists.");
  if (status === 409) return detailFromError(error, "That email is already allowlisted.");
  return detailFromError(error, `Could not ${action} known sender.`);
};

export default function KnownSendersManager({ householdId }) {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addValue, setAddValue] = useState("");
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [action, setAction] = useState("");

  const loadSenders = useCallback(async () => {
    if (!householdId) {
      setSenders([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await listKnownSenders(householdId);
      setSenders(Array.isArray(rows) ? rows : []);
      setError("");
    } catch (requestError) {
      setError(mutationMessage(requestError, "load"));
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    loadSenders();
  }, [loadSenders]);

  const resetInlineState = () => {
    setEditId(null);
    setEditValue("");
    setDeleteId(null);
  };

  const beginEdit = (sender) => {
    setError("");
    setDeleteId(null);
    setEditId(sender.id);
    setEditValue(sender.email);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditValue("");
  };

  const onAdd = async (event) => {
    event.preventDefault();
    if (!householdId || !addValue.trim() || action) return;
    setAction("add");
    setError("");
    resetInlineState();
    try {
      await addKnownSender(householdId, addValue);
      await loadSenders();
      setAddValue("");
    } catch (requestError) {
      setError(mutationMessage(requestError, "add"));
    } finally {
      setAction("");
    }
  };

  const onSaveEdit = async (senderId) => {
    if (!householdId || !editValue.trim() || action) return;
    setAction(`edit:${senderId}`);
    setError("");
    setDeleteId(null);
    try {
      await editKnownSender(householdId, senderId, editValue);
      await loadSenders();
      cancelEdit();
    } catch (requestError) {
      setError(mutationMessage(requestError, "save"));
    } finally {
      setAction("");
    }
  };

  const onConfirmDelete = async (senderId) => {
    if (!householdId || action) return;
    setAction(`delete:${senderId}`);
    setError("");
    try {
      await removeKnownSender(householdId, senderId);
      await loadSenders();
      setDeleteId(null);
      if (editId === senderId) cancelEdit();
    } catch (requestError) {
      setError(mutationMessage(requestError, "delete"));
    } finally {
      setAction("");
    }
  };

  return (
    <section data-testid="known-senders-manager" className="rounded-sm border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-sm bg-secondary p-2 text-muted-foreground">
          <EnvelopeSimple size={18} />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Comms</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">Known senders</h2>
          <p className="mt-1 text-sm text-muted-foreground">These senders may propose desk candidates.</p>
        </div>
      </div>

      <form onSubmit={onAdd} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Input
          data-testid="known-sender-input"
          value={addValue}
          onChange={(event) => {
            setAddValue(event.target.value);
            if (error) setError("");
          }}
          placeholder="name@example.com"
          className="rounded-sm"
          disabled={!householdId || !!action}
        />
        <Button
          data-testid="known-sender-add"
          type="submit"
          className="rounded-sm font-semibold sm:shrink-0"
          disabled={!householdId || !addValue.trim() || !!action}
        >
          {action === "add" ? "Adding…" : "Add sender"}
        </Button>
      </form>

      {error ? (
        <div className="mt-3 flex items-start gap-2 rounded-sm border border-destructive/30 bg-destructive/5 px-3 py-2">
          <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : null}

      <div className="mt-5">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading known senders…</p>
        ) : senders.length === 0 ? (
          <p className="text-sm text-muted-foreground">No known senders yet.</p>
        ) : (
          <div className="space-y-3">
            {senders.map((sender) => {
              const isEditing = editId === sender.id;
              const isDeleteConfirming = deleteId === sender.id;
              const isSavingEdit = action === `edit:${sender.id}`;
              const isDeleting = action === `delete:${sender.id}`;

              return (
                <div key={sender.id} className="rounded-sm border border-border bg-secondary/20 px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <Input
                            value={editValue}
                            onChange={(event) => {
                              setEditValue(event.target.value);
                              if (error) setError("");
                            }}
                            className="rounded-sm"
                            disabled={!!action}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              className="rounded-sm font-semibold"
                              disabled={!editValue.trim() || !!action}
                              onClick={() => onSaveEdit(sender.id)}
                            >
                              {isSavingEdit ? "Saving…" : "Save"}
                            </Button>
                            <Button type="button" variant="ghost" className="rounded-sm" disabled={!!action} onClick={cancelEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="break-all font-medium text-foreground">{sender.email}</p>
                      )}

                      {isDeleteConfirming ? (
                        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm text-muted-foreground">
                            This sender will no longer propose desk candidates.
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              className="rounded-sm font-semibold"
                              disabled={!!action}
                              onClick={() => onConfirmDelete(sender.id)}
                            >
                              {isDeleting ? "Removing…" : "Confirm"}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="rounded-sm"
                              disabled={!!action}
                              onClick={() => setDeleteId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {!isEditing ? (
                      <div className="flex items-center gap-2 sm:ml-4">
                        <Button
                          data-testid="known-sender-edit"
                          type="button"
                          variant="outline"
                          className="rounded-sm"
                          disabled={!!action}
                          onClick={() => beginEdit(sender)}
                        >
                          <PencilSimple size={15} className="mr-1" /> Edit
                        </Button>
                        <Button
                          data-testid="known-sender-delete"
                          type="button"
                          variant={isDeleteConfirming ? "secondary" : "outline"}
                          className="rounded-sm"
                          disabled={!!action}
                          onClick={() => {
                            setError("");
                            cancelEdit();
                            setDeleteId(isDeleteConfirming ? null : sender.id);
                          }}
                        >
                          {isDeleteConfirming ? <X size={15} className="mr-1" /> : <Trash size={15} className="mr-1" />}
                          Delete
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
